import {
    RedTariff,
    YoungTariff,
} from '@vfde-sails/constants';
import {
    AbstractedTariffOffer,
    getTotalAmountOfDataVolumeOverRuntime,
    getTotalAmountToPayOverRuntime,
} from '@vfde-sails/glados-v2';
import { DATA_AMOUNT_UNLIMITED } from 'Container/App/constants';
import { SailsTariffOverviewStorage } from 'Container/App/interface';
import NAMESPACE from 'Constant';

/**
 * Finds best matching tariff on channel/gigakombi switch if current tariff is not available
 */
export const findEquivalentTariff = (
    activeSubscription: NonNullable<NonNullable<SailsTariffOverviewStorage[typeof NAMESPACE]>['previousOffer']>,
    availableSubscriptions: ReadonlyArray<AbstractedTariffOffer<RedTariff | YoungTariff>>,
): RedTariff | YoungTariff => {
    const currentDataAmount = getTotalAmountOfDataVolumeOverRuntime(
        activeSubscription.dataVolumeWithoutDiscounts,
        activeSubscription.dataVolumeWithDiscounts,
    );

    // If current tariff is unlimited, find the tariff with the highest data volume (or another unlimited)
    if (currentDataAmount <= DATA_AMOUNT_UNLIMITED) {
        const offerWithHighestData = [getOfferWithHighestData(availableSubscriptions)];

        return getFirstGroupIdFromOfferArray(offerWithHighestData);
    }

    // Find the offers with the closest data volume amount to original tariff
    const closestOffersByData = getClosestOffers(
        currentDataAmount,
        availableSubscriptions,
        (offer: AbstractedTariffOffer) =>
            getTotalAmountOfDataVolumeOverRuntime(offer.dataVolumeWithoutDiscounts, offer.dataVolumeWithDiscounts),
    );

    // Only one offer found, return it
    if (closestOffersByData.length === 1) {
        return getFirstGroupIdFromOfferArray(closestOffersByData);
    }

    // More than one offer have same data volume distance to original tariff, compare them by price
    const currentPrice = getTotalAmountToPayOverRuntime(
        activeSubscription.priceMonthlyTariffWithoutDiscounts,
        activeSubscription.priceMonthlyTariffWithDiscounts,
    );
    const closestOffersByPrice = getClosestOffers(
        currentPrice!,
        closestOffersByData,
        (offer: AbstractedTariffOffer) =>
            getTotalAmountToPayOverRuntime(offer.priceMonthlyTariffWithoutDiscounts, offer.priceMonthlyTariffWithDiscounts),
    );

    // Only one offer found, return it
    if (closestOffersByPrice.length === 1) {
        return getFirstGroupIdFromOfferArray(closestOffersByPrice);
    }

    // Still more than one, sort them by data amount
    const closestOffers = closestOffersByPrice
        .toSorted((a, b) => {
            const dataA = getTotalAmountOfDataVolumeOverRuntime(a.dataVolumeWithoutDiscounts, a.dataVolumeWithDiscounts);
            const dataB = getTotalAmountOfDataVolumeOverRuntime(b.dataVolumeWithoutDiscounts, b.dataVolumeWithDiscounts);

            return dataB - dataA;
        });

    return getFirstGroupIdFromOfferArray(closestOffers);
};

/**
 * Get the closest offers by a flexible compare property
 * @param currentValue The current value to compare offers to
 * @param offers The offers to compare
 * @param getCompareValueFromOffer Callback function to get the property to compare
 */
export const getClosestOffers = (
    currentValue: number,
    offers: ReadonlyArray<AbstractedTariffOffer<RedTariff | YoungTariff>>,
    getCompareValueFromOffer: (obj: AbstractedTariffOffer<RedTariff | YoungTariff>) => number | undefined,
): ReadonlyArray<AbstractedTariffOffer<RedTariff | YoungTariff>> => {
    let closestOffers: AbstractedTariffOffer<RedTariff | YoungTariff>[] = [];
    let diff;

    for (const offer of offers) {
        const compareValue = getCompareValueFromOffer(offer);

        const tmpDiff = Math.abs(currentValue - compareValue!);

        // Identical offer in data volume or price, return it
        if (tmpDiff === 0) {
            return [offer];
        }

        // No difference yet, or previous difference is bigger than this one, create the offer array
        if (!diff || tmpDiff < diff) {
            diff = tmpDiff;
            closestOffers = [offer];
        }
        else if (tmpDiff === diff) {
            // Another offer with the same difference - add it to the array
            closestOffers.push(offer);
        }
    }

    return closestOffers;
};

/**
 * Return first subscription group ID from array of offers
 */
export const getFirstGroupIdFromOfferArray = (
    offers: ReadonlyArray<Pick<AbstractedTariffOffer<RedTariff | YoungTariff>, 'tariffId'>>,
): AbstractedTariffOffer<RedTariff | YoungTariff>['tariffId'] => offers[0].tariffId;

/**
 * Find either unlimited tariff or the tariff with the highest data volume
 */
export const getOfferWithHighestData = (
    offers: ReadonlyArray<AbstractedTariffOffer<RedTariff | YoungTariff>>,
): AbstractedTariffOffer<RedTariff | YoungTariff> => {
    for (const offer of offers) {
        if ((offer.dataVolumeWithDiscounts || [offer.dataVolumeWithoutDiscounts])
            .some(dataVolume => dataVolume.unlimited)) {
            return offer;
        }
    }

    return offers?.reduce((max, offer) =>
        getTotalAmountOfDataVolumeOverRuntime(
            max.dataVolumeWithoutDiscounts,
            max.dataVolumeWithDiscounts,
        ) > getTotalAmountOfDataVolumeOverRuntime(
            offer.dataVolumeWithoutDiscounts,
            offer.dataVolumeWithDiscounts,
        ) ? max : offer);
};
